import * as diff from 'diff'

export const repeatTabs = (indentation: number): string =>
  '\t'.repeat(indentation)

export const addIndentationToLines = (
  text: string,
  indentation: number,
): string => {
  const lines = text.split('\n')
  const indentedLines = lines
    .map((line) => repeatTabs(indentation).concat(line))
    .join('\n')

  return indentedLines
}

export const addPrefixesToLines = (text: string, prefix: string): string => {
  const hasNewLineAtTheEnd = text.endsWith('\n')
  const lines = text.split('\n')
  // If the text ends with a new line, the last element of the array will be an empty string,
  // so we remove it to avoid adding an extra new line at the end of the text.
  if (hasNewLineAtTheEnd) {
    lines.pop()
  }

  const prefixedLines = lines.map((line) => `${prefix} ${line}`).join('\n')

  return prefixedLines.concat(hasNewLineAtTheEnd ? '\n' : '')
}

export const maximizeLineSize = (text: string, lineSize: number): string => {
  const words = text.split(' ')
  let currentLine = ''
  const lines = []
  for (const word of words) {
    currentLine = currentLine.concat(word, ' ')
    if (currentLine.length > lineSize) {
      lines.push(currentLine)
      currentLine = ''
    }
  }
  lines.push(currentLine)
  return lines.join('\n')
}

export const maximizeLinesSizeInText = (
  text: string,
  lineSize: number,
): string => {
  const lines = text.split('\n')
  const maximizedLines = lines.map((line) => {
    if (line.length < lineSize) return line
    return maximizeLineSize(line, lineSize)
  })
  return maximizedLines.join('\n')
}

export const getDiffOfTexts = ({
  from,
  to,
  lineSize,
}: {
  from: string
  to: string
  lineSize: number
}): string => {
  const fromWithNewLine = from.endsWith('\n') ? from : from.concat('\n')
  const toWithNewLine = to.endsWith('\n') ? to : to.concat('\n')
  const diffResult = diff.diffLines(fromWithNewLine, toWithNewLine)
  const diffTextWithPrefixes = diffResult
    .map((part) => {
      const textWithMaximizedLineSize = maximizeLinesSizeInText(
        part.value,
        lineSize,
      )
      if (part.added) {
        return addPrefixesToLines(textWithMaximizedLineSize, '++')
      }
      if (part.removed) {
        // Add a new line if the last character is not a new line, to separate the added and removed parts,
        // otherwise, the added part will be attached to the removed part.
        if (!textWithMaximizedLineSize.endsWith('\n')) {
          return addPrefixesToLines(textWithMaximizedLineSize, '--').concat(
            '\n',
          )
        }
        return addPrefixesToLines(textWithMaximizedLineSize, '--')
      }
      return addPrefixesToLines(textWithMaximizedLineSize, '  ')
    })
    .join('')

  // remove last added line
  return diffTextWithPrefixes.slice(0, -1)
}

export const generateChangeText = ({
  from,
  to,
  label,
  tabs = 3,
}: {
  from: string
  to: string
  label: string
  tabs?: number
}): string => `${repeatTabs(tabs)}${label}: ${from} -> ${to}`

export const sortMapToArray = <T>(map: Map<string, T>): [string, T][] =>
  Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b))
